import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getCampaigns, MarketingCampaign, proposeCampaign } from '../../../api/services/marketingService';
import CampaignList from './CampaignList';
import CampaignPage from './CampaignPage';
import CampaignComposer from './CampaignComposer';
import CampaignReview from './CampaignReview';
import CampaignIdeas from './CampaignIdeas';
import { KIND_LABEL } from './shared';
import { Card, LinkButton, SectionLabel } from '../../../components/ui/dg';

/**
 * Campagnes (Communication › Campagnes) : la liste à gauche, la relecture de
 * la campagne choisie à droite (`?c=12`). La page complète, avec la
 * discussion et l'historique, reste à l'adresse `?campagne=12` (!12).
 * L'API en propose (lundi, jeudi, avant les fêtes), l'IA suggère des idées
 * d'après le calendrier (`?idees=1`), l'équipe en écrit ;
 * une autre personne que l'auteur approuve, puis la campagne part.
 */
const Campaigns = () => {
  const { token, user } = useAuth();
  const meId = String((user as any)?.id_user || (user as any)?.id || '');
  const [params, setParams] = useSearchParams();
  const openRef = params.get('campagne');
  const creating = params.get('nouvelle') === '1';
  const showingIdeas = params.get('idees') === '1';

  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [proposing, setProposing] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setCampaigns(await getCampaigns(token));
    } catch (err: any) {
      setError(err.message || 'Campagnes indisponibles.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const go = (next: Record<string, string | null>) => {
    const updated = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => (v === null ? updated.delete(k) : updated.set(k, v)));
    setParams(updated);
  };
  const openCampaign = (c: MarketingCampaign) => go({ c: String(c.number || c.id_campaign), nouvelle: null });

  const upsert = (c: MarketingCampaign) => setCampaigns((rows) => (rows.some((r) => r.id_campaign === c.id_campaign) ? rows.map((r) => (r.id_campaign === c.id_campaign ? c : r)) : [c, ...rows]));

  const propose = async (kind?: string) => {
    if (!token) return;
    setProposeOpen(false);
    setProposing(true);
    setNotice(null);
    try {
      const c = await proposeCampaign(token, kind);
      if (!c) setNotice('Rien à proposer pour ce genre en ce moment (tout est déjà passé récemment).');
      else { upsert(c); openCampaign(c); }
    } catch (err: any) {
      setNotice(err.message || 'Proposition impossible.');
    } finally {
      setProposing(false);
    }
  };

  // Sélection de la liste (`?c=12`), distincte de la page complète (`?campagne=12`).
  const selectedRef = params.get('c');
  const waiting = campaigns.filter((c) => c.status === 'pending');
  const selected = campaigns.find((c) => String(c.number || c.id_campaign) === selectedRef || c.id_campaign === selectedRef)
    || waiting[0] || campaigns.find((c) => c.status === 'approved') || campaigns[0] || null;
  const select = (c: MarketingCampaign) => go({ c: String(c.number || c.id_campaign) });

  if (creating && token) {
    return (
      <main className="flex-1 overflow-y-auto px-4 pb-6 pt-1 lg:px-7">
        <Card className="p-5 lg:p-7">
          <CampaignComposer token={token} onCancel={() => go({ nouvelle: null })} onCreated={(c) => { upsert(c); go({ nouvelle: null, c: String(c.number || c.id_campaign) }); }} />
        </Card>
      </main>
    );
  }

  if (showingIdeas && !openRef && token) {
    return (
      <main className="flex-1 overflow-y-auto px-4 pb-6 pt-1 lg:px-7">
        <CampaignIdeas token={token} onBack={() => { go({ idees: null }); load(); }}
          onOpenCampaign={(r) => { load(); go({ idees: null, campagne: r }); }} />
      </main>
    );
  }

  if (openRef && token) {
    return (
      <main className="flex-1 overflow-y-auto px-4 pb-6 pt-1 lg:px-7">
        <CampaignPage key={openRef} campaignRef={openRef} token={token} meId={meId} onBack={() => { go({ campagne: null }); load(); }} onChanged={upsert} />
      </main>
    );
  }

  return (
    <main className="flex min-h-0 flex-1 flex-col px-4 pb-6 pt-1 lg:px-7">
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 lg:grid-cols-[minmax(320px,0.5fr)_minmax(0,1fr)]">
        <Card className="flex min-h-0 flex-col overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-7 pb-3 pt-6">
            <SectionLabel>Campagnes</SectionLabel>
            <span className="relative flex items-center gap-4">
              <LinkButton onClick={() => go({ idees: '1', nouvelle: null })}>Idées</LinkButton>
              <LinkButton disabled={proposing} onClick={() => setProposeOpen((v) => !v)}>{proposing ? 'Proposition…' : 'Proposer'}</LinkButton>
              <LinkButton onClick={() => go({ nouvelle: '1', campagne: null })}>Nouvelle</LinkButton>
              {proposeOpen && (
                <div className="absolute right-0 top-7 z-20 w-56 rounded-2xl border border-[#EEF0F4] bg-white p-1.5 shadow-[0_10px_30px_rgba(15,23,42,0.15)]">
                  {[{ kind: undefined as string | undefined, label: 'Au choix de l’API' }, ...Object.entries(KIND_LABEL).filter(([k]) => k !== 'custom').map(([kind, label]) => ({ kind, label }))].map((o) => (
                    <button key={o.kind ?? 'auto'} type="button" onClick={() => propose(o.kind)} className="w-full rounded-xl px-3 py-2 text-left text-[13.5px] text-[#1E293B] hover:bg-[#F7F7FB]">{o.label}</button>
                  ))}
                </div>
              )}
            </span>
          </div>
          {notice && <p className="mx-6 mb-2 rounded-xl bg-[#FFF8EB] px-3 py-2 text-[12.5px] text-[#92400E]">{notice}</p>}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <CampaignList campaigns={campaigns} loading={loading} error={error} selectedId={selected?.id_campaign || null} onRetry={load} onSelect={select} />
          </div>
        </Card>
        <Card className="flex min-h-[560px] flex-col overflow-hidden">
          {selected && token ? (
            <CampaignReview campaign={selected} token={token} meId={meId} onChanged={upsert}
              onOpenFull={() => go({ campagne: String(selected.number || selected.id_campaign) })} />
          ) : (
            <p className="m-auto px-6 text-center text-[13.5px] text-[#6B7280]">{loading ? 'Chargement…' : 'Choisissez une campagne, ou écrivez-en une nouvelle.'}</p>
          )}
        </Card>
      </div>
    </main>
  );
};

export default Campaigns;
